// WishPhasesPanel — phase completion mode UI (W35 §4).
//
// Per docs/spec/wishlist/W35.2-SUBRESOURCE-UI-POLISH.md §1.4:
// - horizontal stepper (vertical on mobile): phase dot + title + percent
//   + ✓ if completed
// - "+ 新增 phase" trigger at the bottom
// - empty state: "還沒有設定階段。"
//
// Sized for the AGENTS.md §3 #10 sub-component budget (~6KB).

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Circle, CircleCheck, ListChecks, Plus, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ErrorState } from "@/components/primitives/ErrorState";
import { useToast } from "@/components/ui/toast";
import {
  completePhase,
  createPhase,
  deletePhase,
  listPhases,
  type WishPhaseOut,
} from "@/lib/api/wishlist";
import { queryKeys } from "@/lib/queries/keys";
import { cn } from "@/lib/utils/cn";

export interface WishPhasesPanelProps {
  wishId: string;
  className?: string;
}

export function WishPhasesPanel({ wishId, className }: WishPhasesPanelProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", percent: 0 });

  const query = useQuery({
    queryKey: ["wishes", "detail", wishId, "phases"],
    queryFn: () => listPhases(wishId),
    enabled: Boolean(wishId),
    staleTime: 30_000,
  });

  const create = useMutation({
    mutationFn: () =>
      createPhase(wishId, {
        title: form.title.trim(),
        percent: form.percent,
      }),
    onSuccess: () => {
      setAdding(false);
      setForm({ title: "", percent: 0 });
      void queryClient.invalidateQueries({
        queryKey: ["wishes", "detail", wishId, "phases"],
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
    onError: () => toast.push({ message: "新增階段失敗", variant: "danger" }),
  });

  const complete = useMutation({
    mutationFn: (phaseId: string) => completePhase(wishId, phaseId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["wishes", "detail", wishId, "phases"],
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });

  const del = useMutation({
    mutationFn: (phaseId: string) => deletePhase(wishId, phaseId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["wishes", "detail", wishId, "phases"],
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });

  const phases: WishPhaseOut[] = query.data ?? [];

  return (
    <Card
      data-component="WishPhasesPanel"
      data-testid="wish-phases-panel"
      className={cn("p-s-4", className)}
    >
      <header className="flex items-center justify-between gap-2 mb-s-3">
        <h3 className="font-display text-h3 text-ink-1 flex items-center gap-2">
          <ListChecks className="h-4 w-4" aria-hidden />
          階段進度
          <span className="text-meta text-ink-3 font-normal">
            {phases.length}
          </span>
        </h3>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setAdding((v) => !v)}
          data-testid="wish-phases-add-toggle"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          {adding ? "取消" : "新增階段"}
        </Button>
      </header>
      {adding ? (
        <form
          data-testid="wish-phases-add-form"
          className="flex flex-col gap-s-2 mb-s-3 p-s-3 bg-paper-deep rounded-md"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <input
            type="text"
            value={form.title}
            onChange={(e) =>
              setForm((f) => ({ ...f, title: e.target.value }))
            }
            placeholder="階段名稱 (例: 規劃)"
            maxLength={100}
            required
            data-testid="wish-phases-add-title"
            className="w-full px-s-3 py-s-2 rounded-md border border-hairline bg-paper-elevated text-body-sm text-ink-1"
          />
          <label className="text-meta text-ink-2">
            完成度 (%)
            <input
              type="number"
              min={0}
              max={100}
              value={form.percent}
              onChange={(e) =>
                setForm((f) => ({ ...f, percent: Number(e.target.value) }))
              }
              data-testid="wish-phases-add-percent"
              className="w-full mt-1 px-s-3 py-s-2 rounded-md border border-hairline bg-paper-elevated text-body-sm text-ink-1"
            />
          </label>
          <Button
            type="submit"
            disabled={create.isPending || !form.title.trim()}
            data-testid="wish-phases-add-submit"
            className="press min-h-11 px-s-4 rounded-full bg-ink-1 text-paper"
          >
            確認新增
          </Button>
        </form>
      ) : null}
      {query.isLoading ? (
        <p className="text-meta text-ink-3">載入中…</p>
      ) : query.isError ? (
        // W54.0 B1 — a failed read is not 「還沒有設定階段」. The empty state
        // says there is no progress yet; the error state says we could not
        // find out. Only the first one is worth acting on.
        <ErrorState onRetry={() => void query.refetch()} />
      ) : phases.length === 0 ? (
        <p className="text-meta text-ink-3">還沒有設定階段。</p>
      ) : (
        <ol
          data-component="WishPhaseList"
          className="flex flex-col gap-s-2"
        >
          {phases.map((p) => {
            const done = p.completed_at !== null;
            return (
              <li
                key={p.phase_id}
                data-component="WishPhaseRow"
                data-testid={`wish-phase-row-${p.phase_id}`}
                data-completed={done ? "true" : "false"}
                className="flex items-center gap-2 p-s-2 rounded-md bg-paper-deep"
              >
                {done ? (
                  <CircleCheck
                    className="h-4 w-4 shrink-0 text-ink-1"
                    aria-hidden
                  />
                ) : (
                  <Circle
                    className="h-4 w-4 shrink-0 text-ink-3"
                    aria-hidden
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p
                    className={cn(
                      "text-body-sm",
                      done
                        ? "text-ink-3 line-through"
                        : "text-ink-1",
                    )}
                  >
                    {p.title}
                  </p>
                  <p className="text-meta text-ink-3 tabular-nums">
                    {p.percent}%
                  </p>
                </div>
                {!done ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => complete.mutate(p.phase_id)}
                    disabled={complete.isPending}
                    data-testid={`wish-phase-row-complete-${p.phase_id}`}
                    className="press min-h-11 min-w-11 px-s-3 rounded-full"
                  >
                    完成
                  </Button>
                ) : null}
                <button
                  type="button"
                  onClick={() => del.mutate(p.phase_id)}
                  disabled={del.isPending}
                  data-testid={`wish-phase-row-delete-${p.phase_id}`}
                  aria-label="刪除階段"
                  className="press shrink-0 min-h-11 min-w-11 flex items-center justify-center rounded-md text-ink-3 hover:bg-paper-calm disabled:opacity-50"
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden />
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}